/*
 *   File : upsert-admin.js
 *   Author : https://evoqins.com
 *   Description : upsert admin component
 *   Integrations : sub-admin/modules-data, sub-admin/details, sub-admin/upsert-admin
 *   Version : 1.0.2
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import {
  CheckboxSelect,
  CustomFileUpload,
  CustomSelectMenu,
  Input,
} from "../../Components/FormElements";
import { PrimaryButton } from "../../Components/Buttons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { APIService } from "../../Service";
import { Loader } from "../../Components/Other";

export default function UpsertAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ---------------------------
  // FORM STATE
  // ---------------------------
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState();
  const [moduleOptions, setModuleOptions] = useState([]);
  const [coverImage, setCoverImage] = useState(null);

  // ---------------------------
  // LOADER STATE
  // ---------------------------
  const [loader, setLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);

  // ---------------------------
  // ERROR STATE
  // ---------------------------
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [roleError, setRoleError] = useState("");
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    if (id) dispatch(setPageTitle(["Manage Sub Admin", "Edit Sub Admin"]));
    else dispatch(setPageTitle(["Manage Sub Admin", "Add Sub Admin"]));
    dispatch(setNavLink(["/manage-sub-admin"]));
    dispatch(setSearchEnabled(false));

    _fetchModuleOptions();

    if (id) {
      _fetchAdminDetails();
    }
  }, [id]);

  // ---------------------------
  // FETCH MODULE OPTIONS
  // ---------------------------
  const _fetchModuleOptions = () => {
    APIService(false, "sub-admin/admin_type")
      .then((res) => {
        if (res.status_code === 200) {
          setModuleOptions(res.data[0][0].options);
          console.log(res.data[0][0].options);
        }
      })
      .catch((error) => {
        console.error("Error fetching module options:", error);
        toast.error("Failed to load module options");
      });
  };

  // ---------------------------
  // FETCH ADMIN DETAILS (FOR EDIT)
  // ---------------------------
  const _fetchAdminDetails = () => {
    setLoader(true);

    const payload = {
      id,
    };

    APIService(true, "sub-admin/details", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          const adminData = res.data.admin_data;
          console.log(res);

          setName(adminData.admin_name || "");
          setEmail(adminData.admin_email || "");
          setPhone(adminData.customer_phone || "");
          setRole(
            adminData.modules?.map((module) => ({
              label: module.name,
              value: module.module_id,
            })) || [],
          );

          // Prepopulate image if exists
          if (adminData.image) {
            setCoverImage(adminData.image);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching admin details:", error);
        toast.error("Failed to load admin details");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  // ---------------------------
  // IMAGE HANDLERS
  // ---------------------------
  const handleCoverChange = (file) => {
    setCoverImage(file);
    setImageError("");
  };

  const handleCoverClear = () => {
    setCoverImage(null);
  };

  const handleCoverUploadComplete = (uploadedUrl) => {
    setCoverImage(uploadedUrl);
    setImageError("");
  };

  // ---------------------------
  // VALIDATION HANDLER
  // ---------------------------
  const _handleSubmit = () => {
    let isValid = true;

    // NAME
    if (name.trim().length === 0) {
      setNameError("Name is required");
      isValid = false;
    }

    // EMAIL
    let emailRegex = /^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/;
    if (email.trim().length === 0) {
      setEmailError("Email is required");
      isValid = false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email");
      isValid = false;
    }

    // PHONE
    let phoneRegex = /^[0-9]{10}$/;
    if (phone.trim().length === 0) {
      setPhoneError("Phone number is required");
      isValid = false;
    } else if (!phoneRegex.test(phone)) {
      setPhoneError("Phone number must be 10 digits");
      isValid = false;
    }

    // MULTI SELECT
    if (!role) {
      setRoleError("Select a module");
      isValid = false;
    }
    if (!coverImage) {
      setImageError("Upload an Image");
      isValid = false;
    }

    if (!isValid) return;

    _submitForm();
  };

  // ---------------------------
  // SUBMIT FORM (UPSERT API)
  // ---------------------------
  const _submitForm = () => {
    setSubmitLoader(true);

    let admin_type;

    if (Array.isArray(role)) {
      admin_type = role?.[0]?.value || null;
    } else {
      admin_type = role?.value || null;
    }

    const payload = {
      type: id ? 2 : 1,
      admin_id: id ? parseInt(id) : null,
      username: name,
      email: email,
      mobile_number: phone,
      admin_type: admin_type,
      image: coverImage || null,
    };

    console.log(payload, role);

    APIService(true, "sub-admin/upsert-admin", payload)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(
            id ? "Admin updated successfully" : "Admin added successfully",
          );
          navigate("/manage-sub-admin");
        } else {
          toast.error(res.message || "Failed to save admin");
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        toast.error("Failed to save admin");
      })
      .finally(() => {
        setSubmitLoader(false);
      });
  };

  if (loader) {
    return <Loader />;
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="e-glass-card rounded-16 d-flex flex-column p-24 bg-transparent">
          <div className="e-glass-card rounded-16 d-flex flex-column p-24">
            <h3 className="fw-500 fs-18 lh-20 text-eerie-black">
              {id ? "Edit" : "Add"} Sub Admin
            </h3>

            <div className="row">
              <div className="col-xl-6 col-lg-7 col-md-9 col-12">
                <div className="d-flex flex-column pt-3">
                  {/* NAME */}
                  <Input
                    id="name"
                    type="text"
                    label="Name"
                    placeholder="Enter a name"
                    require={true}
                    value={name}
                    error={nameError}
                    className="w-100 px-3 py-10"
                    handleChange={(v) => {
                      setName(v);
                      setNameError("");
                    }}
                    onKeyPress={_handleSubmit}
                    autoFocus={true}
                  />

                  {/* IMAGE UPLOAD */}
                  <div className="d-flex flex-column w-100">
                    <p className="mb-0 fw-400 fs-14">
                      Upload image <span className="text-cg-red">*</span>
                    </p>
                    <CustomFileUpload
                      type={2}
                      fileSize={3}
                      error={imageError}
                      file={coverImage}
                      handleChange={handleCoverChange}
                      handleClear={handleCoverClear}
                      onUploadComplete={handleCoverUploadComplete}
                    />
                  </div>
                  {/* <div className="d-flex w-100 flex-column">
                    <CheckboxSelect
                      label="Role"
                      options={moduleOptions}
                      placeholder="Select Module"
                      type={1}
                      value={role}
                      error={roleError}
                      onChange={(selected) => {
                        setRole(selected);
                        setRoleError("");
                      }}
                    />
                  </div> */}

                  <div className="d-flex flex-column pt-1 mb-2">
                    <p className="mb-0 fw-400 fs-14 pb-1">
                      Role <span className="text-cg-red">*</span>
                    </p>

                    <CheckboxSelect
                      options={moduleOptions}
                      placeholder="Select Module"
                      type={1}
                      value={role}
                      error={roleError}
                      onChange={(selected) => {
                        setRole(selected);
                        setRoleError("");
                      }}
                    />
                  </div>

                  {/* EMAIL */}
                  <Input
                    id="email"
                    type="email"
                    label="Email"
                    placeholder="Add Email"
                    require={true}
                    value={email}
                    error={emailError}
                    onKeyPress={_handleSubmit}
                    className="w-100 px-3 py-10"
                    handleChange={(v) => {
                      setEmail(v);
                      setEmailError("");
                    }}
                  />

                  {/* PHONE */}
                  <Input
                    id="phone"
                    type="text"
                    label="Phone"
                    placeholder="Add phone no"
                    require={true}
                    value={phone}
                    error={phoneError}
                    onKeyPress={_handleSubmit}
                    className="w-100 px-3 py-10"
                    handleChange={(v) => {
                      setPhone(v);
                      setPhoneError("");
                    }}
                  />

                  {/* BUTTONS */}
                  <div className="row row-cols-2 pt-24">
                    <div className="col">
                      <PrimaryButton
                        name="Cancel"
                        ClassName="rounded-pill bg-transparent border-oxford-blue lh-24 py-3 text-oxford-blue w-100 fs-16 fw-500"
                        onPress={() => navigate("/manage-sub-admin")}
                        disabled={submitLoader}
                      />
                    </div>
                    <div className="col">
                      <PrimaryButton
                        name={
                          submitLoader ? "Saving..." : id ? "Update" : "Add"
                        }
                        ClassName="rounded-pill bg-oxford-blue lh-24 py-3 text-white w-100 fs-16 fw-500"
                        onPress={_handleSubmit}
                        disabled={submitLoader}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
